import React, {
  ComponentProps,
  MouseEventHandler,
  useCallback,
} from 'react'

import {
  tripSavedItemBookNowClicked,
  tripSavedItemOfferDetailsClicked,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { DetailItemCardVariant } from 'tripPlanner/components/DetailItemCard/common/types'
import { SEARCH_FLIGHTS_COPY, VIEW_OFFER_COPY } from 'tripPlanner/config'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useRestoreCart from 'tripPlanner/hooks/useRestoreCart'
import { BOOK_NOW_BUTTON_TEXT } from 'tripPlanner/hooks/useRestoreCartButtonProps'
import { LETripItem } from 'tripPlanner/types/tripItem'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { getIsLuxPlusExclusiveAccess } from 'tripPlanner/utils/luxPlux'
import { addTripIdParam } from 'tripPlanner/utils/url'
import useCanEditTrip from 'tripPlanner/hooks/useCanEditTrip'

interface Props {
  item: LETripItem
  className?: string
  variant: DetailItemCardVariant
  offerLink: string
  hotelOffer?:
    | App.HotelOffer
    | App.BedbankOffer
    | App.BundleOffer
    | App.VillaOffer
}

function LEOfferCTAs({
  item,
  className,
  variant,
  hotelOffer,
  offerLink,
}: Props) {
  const canEdit = useCanEditTrip()

  const { tripId } = item

  const { canRestoreCart, onClick } = useRestoreCart(item, offerLink, {
    hotel: hotelOffer,
  })
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const isLuxPlusExclusiveAccess = getIsLuxPlusExclusiveAccess(item)

  const source =
    variant === 'drawer' ? 'trip_drawer_all_saved_items' : 'trip_summary'
  const modalElement = useModalElementContext()
  const onClickViewButton = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(() => {
    fireInteractionEvent(tripSavedItemOfferDetailsClicked(source))
    if (canEdit) {
      setImmersiveMode({
        tripId,
        sectionId: tripItemSelectionId(item.id),
      })
    }
    modalElement.resolve()
  }, [canEdit, item.id, tripId, modalElement, setImmersiveMode, source])

  const onClickRestoreCartButton = useCallback<
    MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      if (e.currentTarget.textContent === BOOK_NOW_BUTTON_TEXT) {
        fireInteractionEvent(tripSavedItemBookNowClicked(source))
      }
      onClick(e)
    },
    [source, onClick],
  )

  const viewCopy =
    item.type === 'FLIGHT' ? SEARCH_FLIGHTS_COPY : VIEW_OFFER_COPY

  const showBookNowButton = canRestoreCart && variant === 'drawer'

  let viewOfferButtonKind: ComponentProps<typeof TextButton>['kind'] =
    'secondary'
  if (isLuxPlusExclusiveAccess || !showBookNowButton && variant === 'drawer') {
    viewOfferButtonKind = 'primary'
  } else if (canEdit && showBookNowButton) {
    viewOfferButtonKind = 'tertiary'
  }

  return (
    <ProductPaletteProvider
      palette={isLuxPlusExclusiveAccess ? 'lux-plus' : 'default'}
    >
      <Group
        direction="horizontal"
        className={className}
        verticalAlign="center"
        horizontalAlign="stretch"
        tabletHorizontalAlign="space-between"
        gap={12}
      >
        <TextButton
          kind={viewOfferButtonKind}
          to={addTripIdParam(offerLink, tripId, canEdit)}
          target="_blank"
          onClick={onClickViewButton}
          size="medium"
        >
          {viewCopy}
        </TextButton>
        {showBookNowButton && (
          <TextButton
            kind="primary"
            onClick={onClickRestoreCartButton}
            size="medium"
          >
            {BOOK_NOW_BUTTON_TEXT}
          </TextButton>
        )}
      </Group>
    </ProductPaletteProvider>
  )
}

export default LEOfferCTAs
